import { getProductIdFromProductCode } from '@noths/polaris-client-utils';
import { RequireKeys } from '@noths/polaris-dev-ts-recipes';
import { BaseFieldObject } from '@noths/polaris-dev-ts-types';

import { PageType, Product } from 'src/types';
import { ImpressionFieldObject, ProductFieldObject } from 'src/types/googleAnalytics';
import { isCategoryPageGuard, isPartnerPageGuard, isSearchPageGuard } from 'src/utils';
import { ReduxApplicationState } from '../redux/combinedReducer';
import { getDeliveryByEventLabel } from './delivery';

type FieldObjectType<P extends PageType> = ProductFieldObject<P> | ImpressionFieldObject<P>;
type StateProp = RequireKeys<Partial<ReduxApplicationState>, 'navigation' | 'products'>;

const generateFieldObject = (
  product: Product,
  state: StateProp,
  index?: number,
): BaseFieldObject => {
  const {
    purchasable,
    code,
    deliveryByEvent,
    freeDomesticDelivery,
    isNew,
    onSale,
    partner,
    price,
    salePercentage,
    title,
  } = product;
  const { currentPage, items, productsReturnedCount } = state.products;
  const pageProductPosition =
    (index ?? items[currentPage].findIndex((prod) => prod.code === product.code)) + 1;

  /*
    Use page product position if on the 1st page.
    Otherwise, count number of products spanning all previous pages,
    and add page product position to said number.
  */
  const dimension25 =
    currentPage === 1
      ? pageProductPosition
      : pageProductPosition + (currentPage - 1) * productsReturnedCount;

  const dimension22List: string[] = [
    freeDomesticDelivery && 'free uk delivery',
    isNew && 'new',
    onSale && !!salePercentage && `-${salePercentage}% off`,
    !purchasable && 'out of stock',
    deliveryByEvent?.arriveByEvent && getDeliveryByEventLabel(deliveryByEvent.eventName),
  ].filter(Boolean) as string[];

  return {
    brand: partner.name,
    dimension22: dimension22List.join(','),
    dimension25,
    dimension30: String(code),
    dimension31: String(price.amount / 100),
    dimension32: String(price.amount / 100),
    dimension33: onSale ? `${salePercentage}.0%` : 'none',
    dimension34: partner.shortcode,
    id: String(getProductIdFromProductCode(code)),
    name: title,
    position: pageProductPosition,
    price: String(price.amount / 100),
  };
};

const getFieldObject = <P extends PageType = PageType>(
  product: Product,
  state: StateProp,
  index?: number,
): FieldObjectType<P> => generateFieldObject(product, state, index) as FieldObjectType<P>;

const getDimension16PartialFieldObject = (
  fieldObject: Partial<BaseFieldObject>,
  product: Product,
  state: StateProp,
) => {
  const {
    navigation: { pageType },
    products: { category },
  } = state;

  const fieldObj: Partial<BaseFieldObject> = {};

  if (isCategoryPageGuard<FieldObjectType<PageType.Category>>(pageType!, fieldObject)) {
    fieldObj.dimension16 = String(category.id);
  } else if (isPartnerPageGuard<FieldObjectType<PageType.Partner>>(pageType!, fieldObject)) {
    fieldObj.dimension16 = product.partner.name.toLowerCase();
  } else if (isSearchPageGuard<FieldObjectType<PageType.Search>>(pageType!, fieldObject)) {
    fieldObj.dimension16 = '15000';
  }

  return fieldObj;
};

export const getProductFieldObject = <T extends PageType>(
  product: Product,
  state: StateProp,
  index?: number,
): ProductFieldObject<T> => {
  const fieldObject = getFieldObject(product, state, index);

  const dimension16PartialFieldObject = getDimension16PartialFieldObject(
    fieldObject,
    product,
    state,
  ) as ProductFieldObject<T>;

  return {
    ...fieldObject,
    ...dimension16PartialFieldObject,
  } as ProductFieldObject<T>;
};

const getFieldObjectList = (product: Product, state: StateProp) => {
  const {
    navigation: { pageType },
    products,
    products: { category },
  } = state;

  const optionalSponsoredSuffix = product.isSponsored ? ' - sponsored' : '';

  switch (pageType) {
    case PageType.Category:
      return `${PageType.Category} - ${category.name}${optionalSponsoredSuffix}`;
    case PageType.Partner:
      return `${
        PageType.Partner
      } - ${product.partner.name.toLowerCase()}${optionalSponsoredSuffix}`;
    case PageType.Search:
      return `${PageType.Search} - ${products.searchTerm}${optionalSponsoredSuffix}`;
    default:
      return '';
  }
};

export const getImpressionFieldObject = <T extends PageType>(
  product: Product,
  state: StateProp,
  index?: number,
) => {
  const impressionFieldObject = {
    ...getFieldObject(product, state, index),
    list: getFieldObjectList(product, state),
  };

  const dimension16PartialFieldObject = getDimension16PartialFieldObject(
    impressionFieldObject,
    product,
    state,
  ) as ImpressionFieldObject<T>;

  return {
    ...impressionFieldObject,
    ...dimension16PartialFieldObject,
  } as ImpressionFieldObject<T>;
};

export const getImpressionFieldObjects = (state: StateProp) => {
  const { currentPage, items } = state?.products ?? { items: [], currentPage: 0 };
  const products = items[currentPage] || [];

  return products.map((product, index) => getImpressionFieldObject(product, state, index));
};
